// Fire-and-forget email alerts. Posts to the /api/send-alert function, which
// emails the AJM owner via Resend (or, for pilot-facing events, the pilot at
// data.to). Never blocks the UI and never throws — if alerts aren't configured
// yet, the function quietly no-ops.

export type AlertEvent =
  | 'pilot_activated'
  | 'pilot_pending_review'
  | 'pilot_approved'
  | 'pilot_ready'
  | 'flight_logged'
  | 'flight_correction';

const ALERT_API = `${import.meta.env.BASE_URL}api/send-alert`;

export function sendAlert(event: AlertEvent, data: Record<string, unknown>): void {
  try {
    void fetch(ALERT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, data }),
      keepalive: true, // survives navigation / tab close right after the action
    }).catch(() => {});
  } catch {
    /* never let an alert break the app */
  }
}

/**
 * Same endpoint, but awaited and honest about the result.
 *
 * sendAlert() above deliberately swallows everything — right for background
 * owner notifications, wrong for an email the owner pressed a button to send
 * and expects to see confirmed. Returns false if it did not go out, so the
 * caller can avoid recording it as sent.
 */
export async function sendAlertAwait(
  event: AlertEvent,
  data: Record<string, unknown>
): Promise<boolean> {
  try {
    const res = await fetch(ALERT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, data }),
    });
    if (!res.ok) return false;
    // The function returns { ok:false, skipped:true } when Resend isn't
    // configured. That is not a delivery, so don't let it read as one.
    const body = (await res.json().catch(() => null)) as { ok?: boolean } | null;
    return body?.ok === true;
  } catch {
    return false;
  }
}
